<template>
  <div
    class="dropdown fancy-dropdown"
    data-test="graphics-fontDropdown"
  >
    <button
      class="dropdown-btn select"
      data-toggle="dropdown"
      data-test="graphics-fontDropdown-selectedFont"
      :style="selectedFont.fontValue ? { 'font-family': selectedFont.fontValueClass } : {}"
    >
      {{ selectedFont.fontValue || 'Select Font' }}
    </button>

    <ul
      class="dropdown-menu"
      data-test="graphics-fontDropdown-menu"
    >
      <li class="dropdown-menu__search">
        <input
          ref="fontSearch"
          v-model.trim="searchVal"
          type="text"
          class="dropdown-menu__search-input"
          placeholder="Search fonts…"
          data-test="graphics-fontDropdown-searchInput"
          @click.stop
          @keydown.stop
        />
      </li>

      <!-- Recent -->
      <template v-if="!searchVal">
        <span
          v-if="recentFonts.length"
          class="dropdown-menu__label"
        >
          Recent
        </span>

        <li
          v-for="font in recentFonts"
          :key="`recent-${font.fontId}`"
        >
          <a
            href="#"
            class="dropdown-menu-item"
            :style="{ 'font-family': font.fontId }"
            :class="{ selected: currentFontFamily === font.fontId }"
            :data-test="`graphics-fontDropdown-recentFont-${font.fontId}`"
            @click.prevent="applyFont(font.fontId)"
          >
            {{ font.name }}
            <span
              v-if="currentFontFamily === font.fontId"
              class="fa-solid fa-check"
            ></span>
          </a>
        </li>

        <hr
          v-if="recentFonts.length"
          class="dropdown-menu__separator"
        />
      </template>

      <!-- Custom Fonts -->
      <span
        v-if="filteredCustomFonts.length"
        class="dropdown-menu__label"
      >
        Custom Fonts
      </span>

      <li
        v-for="font in filteredCustomFonts"
        :key="font.id"
      >
        <a
          class="dropdown-menu-item"
          :class="{ selected: currentFontFamily === font.fontId }"
          :style="{ 'font-family': font.fontId }"
          :title="font.name"
          :data-test="`graphics-fontDropdown-customFont-${font.id}`"
          :data-entity="`graphics-fontDropdown-customFont-${font.id}`"
          @click.prevent="applyFont(font.fontId)"
        >
          {{ font.name }}
          <span
            v-if="currentFontFamily === font.fontId"
            class="fa-solid fa-check"
          ></span>
        </a>
      </li>

      <hr
        v-if="filteredCustomFonts.length && filteredDefaultFonts.length"
        class="dropdown-menu__separator"
      />

      <!-- Default Fonts -->
      <span
        v-if="filteredDefaultFonts.length"
        class="dropdown-menu__label"
      >
        Default Fonts
      </span>

      <li
        v-for="font in filteredDefaultFonts"
        :key="font.fontId"
      >
        <a
          href="#"
          class="dropdown-menu-item"
          :style="{ 'font-family': font.fontId }"
          :class="{ selected: currentFontFamily === font.fontId }"
          :data-test="`graphics-fontDropdown-defaultFont-${font.fontId}`"
          :data-entity="`graphics-fontDropdown-defaultFont-${font.fontId}`"
          @click.prevent="applyFont(font.fontId)"
        >
          {{ font.name }}
          <span
            v-if="currentFontFamily === font.fontId"
            class="fa-solid fa-check"
          ></span>
        </a>
      </li>

      <div
        v-if="plan.permissions.custom_fonts"
        class="dropdown-menu__footer"
      >
        <input
          ref="file"
          type="file"
          style="display: none"
        />

        <tgb-button
          type="button"
          :variant="plan.permissions.custom_fonts ? 'secondary' : 'disabled'"
          icon="cloud-upload"
          data-test="graphics-fontDropdown-manageFonts"
          data-entity="graphics-fontDropdown-manageFonts"
          @click="plan.permissions.custom_fonts ? openFontUploaderModal() : ''"
        >
          Manage Fonts
        </tgb-button>
      </div>
    </ul>
  </div>
</template>

<script>
import settingsMixins from '../../mixins/settings-mixins';
import EventBus from '../../utils/event-bus';
import TgbButton from '../TgbButton.vue';

export default {
  name: 'FontDropdown',
  components: { TgbButton },
  mixins: [settingsMixins],
  inject: ['toggleFontModal'],

  props: {
    customFonts: {
      type: Array,
      required: true,
    },

    element: {
      type: Object,
      required: true,
    },

    textType: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      defaultFonts: [
        {
          cssClass: 'default',
          name: 'System UI Font',
          fontId:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
        },
        { fontId: 'Anton', name: 'Anton' },
        { fontId: 'Archivo Black', name: 'Archivo Black' },
        { fontId: 'Bebas Neue', name: 'Bebas Neue' },
        { fontId: 'Barlow', name: 'Barlow' },
        { fontId: 'Barlow Condensed', name: 'Barlow Condensed' },
        { fontId: 'Comfortaa', name: 'Comfortaa' },
        { fontId: 'Lato', name: 'Lato' },
        { fontId: 'Libre Baskerville', name: 'Libre Baskerville' },
        { fontId: 'Merriweather', name: 'Merriweather' },
        { fontId: 'Montserrat', name: 'Montserrat' },
        { fontId: 'Mukta', name: 'Mukta' },
        { fontId: 'Nunito', name: 'Nunito' },
        { fontId: 'Open Sans', name: 'Open Sans' },
        { fontId: 'Oswald', name: 'Oswald' },
        { fontId: 'Pacifico', name: 'Pacifico' },
        { fontId: 'Playfair Display', name: 'Playfair Display' },
        { fontId: 'Poppins', name: 'Poppins' },
        { fontId: 'Quantico', name: 'Quantico' },
        { fontId: 'Quicksand', name: 'Quicksand' },
        { fontId: 'Raleway', name: 'Raleway' },
        { fontId: 'Roboto', name: 'Roboto' },
        { fontId: 'Roboto Condensed', name: 'Roboto Condensed' },
        { fontId: 'Rubik', name: 'Rubik' },
        { fontId: 'Source Sans Pro', name: 'Source Sans Pro' },
      ],
      searchVal: '',
      recentFontIds: [],
    };
  },

  computed: {
    plan() {
      return this.$store.state.user.plan;
    },

    allFonts() {
      return [...this.customFonts, ...this.defaultFonts];
    },

    currentFontFamily() {
      let path = 'styles.fontFamily';
      if (this.textType) path = `styles.${this.textType}.fontFamily`;
      return this.getPropertyValue(path);
    },

    selectedFont() {
      let fontFamily = this.getPropertyValue('styles.fontFamily');
      if (this.textType) {
        fontFamily = this.getPropertyValue(`styles.${this.textType}.fontFamily`);
      }

      const selected = this.allFonts.find((font) => [font.fontId, font.name].includes(fontFamily));

      const fontValue = selected ? selected.name : '';

      let fontValueClass = fontFamily;
      if (selected) {
        if (selected.fontId) fontValueClass = selected.fontId;
        else if (selected.font) fontValueClass = selected.font;
      }

      return { fontValue, fontValueClass };
    },

    filteredCustomFonts() {
      if (!this.searchVal) return this.customFonts;
      const q = this.searchVal.toLowerCase();
      return this.customFonts.filter((f) => (f.name || '').toLowerCase().includes(q));
    },

    filteredDefaultFonts() {
      if (!this.searchVal) return this.defaultFonts;
      const q = this.searchVal.toLowerCase();
      return this.defaultFonts.filter((f) => (f.name || '').toLowerCase().includes(q));
    },

    recentFonts() {
      return this.recentFontIds
        .map((id) => this.allFonts.find((f) => f.fontId === id))
        .filter(Boolean)
        .slice(0, 3);
    },
  },

  mounted() {
    try {
      this.recentFontIds = JSON.parse(
        localStorage.getItem('tgb.graphics.recentFonts') || '[]',
      );
    } catch {
      this.recentFontIds = [];
    }

    if (window.$) {
      $(this.$el).on('shown.bs.dropdown', () => {
        this.$nextTick(() => this.$refs.fontSearch && this.$refs.fontSearch.focus());
      });
    }
  },

  methods: {
    resolveFontId(value) {
      // Support legacy cssClass like "default"
      const byCssClass = this.defaultFonts.find((f) => f.cssClass && f.cssClass === value);
      if (byCssClass) return byCssClass.fontId;

      const byNameOrId = this.allFonts.find((f) => f.fontId === value || f.name === value);
      if (byNameOrId) return byNameOrId.fontId;

      return value;
    },

    applyFont(value) {
      const fontId = this.resolveFontId(value);

      let path = 'styles.fontFamily';
      if (this.textType) path = `styles.${this.textType}.fontFamily`;

      this.updateOneElementValue(path, fontId);
      this.trackRecentFont(fontId);

      this.searchVal = '';

      EventBus.emit('text-adjusted');
    },

    trackRecentFont(fontId) {
      const key = 'tgb.graphics.recentFonts';
      const next = [fontId, ...this.recentFontIds.filter((id) => id !== fontId)].slice(0, 3);
      this.recentFontIds = next;
      localStorage.setItem(key, JSON.stringify(next));
    },

    openFontUploaderModal() {
      this.toggleFontModal();
      window.assetmanfn = ({ asset }) => {
        this.applyFont(`f-${asset?._id}`);
        this.toggleFontModal();
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.dropdown-menu {
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;

  &__search {
    position: sticky;
    top: 0;
    z-index: 2;
    width: 100%;
    padding: 1rem 0.5rem;

    background: #fff;
  }

  &__search-input {
    width: 100%;
    line-height: 2;
  }
}
</style>
